import { Injectable, makeStateKey, TransferState } from '@angular/core';
import { PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformServer, isPlatformBrowser } from '@angular/common';
import { RuntimeConfig } from '@ichoosr-core/partner/domain';

const CONFIG_STATE_KEY = makeStateKey<RuntimeConfig>('runtimeConfig');

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return undefined;
};

@Injectable({
  providedIn: 'root',
})
export class RuntimeConfigService extends RuntimeConfig {
  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    private transferState: TransferState,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    super();

    if (isPlatformServer(this.platformId)) {
      const config: RuntimeConfig = {
        ENVIRONMENT_KEY: process.env['ENVIRONMENT_KEY'],
        BUILD_ID: process.env['BUILD_ID'],
        SOURCE_VERSION: process.env['SOURCE_VERSION'],
        LOGGING_SENTRY_DSN: process.env['LOGGING_SENTRY_DSN'],
        CLARITY_PROJECT_ID: process.env['CLARITY_PROJECT_ID'],
        ANALYTICS_CONSOLE_LOGGING: parseBoolean(process.env['ANALYTICS_CONSOLE_LOGGING']),
      };

      this.transferState.set(CONFIG_STATE_KEY, config);

      Object.assign(this, config);
    } else if (isPlatformBrowser(this.platformId)) {
      const config = this.transferState.get<RuntimeConfig>(CONFIG_STATE_KEY, {});

      Object.assign(this, config);

      this.transferState.remove(CONFIG_STATE_KEY);
    }
  }

  private initializationPromise: Promise<void> | null = null;

  loadConfig(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.initializationPromise = fetch('/runtime-config')
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Failed to fetch runtime config');
        })
        .then((config) => {
          Object.assign(this, {
            ...config,
            ANALYTICS_CONSOLE_LOGGING: parseBoolean(config?.ANALYTICS_CONSOLE_LOGGING),
          });
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('[RuntimeConfig] Failed to fetch config:', error);
        });

      return this.initializationPromise;
    }

    return Promise.resolve();
  }
}
